import { useState, useCallback, useEffect, useRef } from 'react'
import req from '@sportninja/common/api/request'
import type { DivisionJob } from './useSchedulerGeneration'

export interface SchedulerFlight {
  id: string
  flight_number: number
  label: string | null
  display_label: string
  start_date: string | null
  end_date: string | null
  status:
    | 'draft'
    | 'pending'
    | 'processing'
    | 'generated'
    | 'partially_generated'
    | 'failed'
    | 'applied'
    | 'published'
    | 'cancelled'
  status_label: string
  is_default: boolean
  sort_order: number
  // Generation fields (was on SchedulerRequest):
  generation_status: string | null
  total_games_count: number
  approved_games_count: number
  satisfaction_score: number | null
  approved_division_uids: string[] | null
  division_dispatch_order: string[] | null
  // Absolute clock cadence (minutes from midnight) generated game starts snap
  // UP to: 0/null = unset (legacy off-grid timing), 30 = half-hour, 60 = hourly.
  game_start_cadence_minutes?: number | null
  jobs?: DivisionJob[]
  submitted_at: string | null
  published_at: string | null
  created_at: string
  updated_at: string
}

export function useSchedulerFlights(scheduleId?: string) {
  const [flights, setFlights] = useState<SchedulerFlight[]>([])
  const [loading, setLoading] = useState(false)
  const hasLoadedRef = useRef(false)

  const fetchFlights = useCallback(async () => {
    if (!scheduleId) {
      return
    }
    if (!hasLoadedRef.current) {
      setLoading(true)
    }
    try {
      const { data } = await req(`/scheduler/schedules/${scheduleId}/flights`)
      const items = data?.data ?? data
      setFlights(Array.isArray(items) ? items : [])
      hasLoadedRef.current = true
    } catch {
      setFlights([])
    } finally {
      setLoading(false)
    }
  }, [scheduleId])

  useEffect(() => {
    fetchFlights()
  }, [fetchFlights])

  return { flights, loading, refetch: fetchFlights }
}
